import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-1.5 shrink-0 whitespace-nowrap stroke-popover-1000 rounded-full text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:shadow-none [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 cursor-pointer select-none',
	{
		variants: {
			variant: {
				default:
					'border border-primary-color-1000 bg-primary-color-1000 text-primary-color-10 hover:bg-primary-color-800 hover:border-primary-color-800 active:bg-primary-color-600 active:border-primary-color-600 disabled:bg-primary-color-50 disabled:border-primary-color-50 disabled:text-popover-400 disabled:stroke-popover-400 hover:shadow-button-hover active:shadow-button-active [&>svg]:stroke-primary-color-100',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border border-popover-100 bg-popover-10 text-popover-1000 active:bg-primary-color-100 hover:shadow-button-hover active:shadow-button-active disabled:stroke-popover-400 disabled:bg-popover-50 disabled:text-popover-400 disabled:border-popover-50',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'hover:bg-primary-color-50 active:bg-primary-color-100 rounded-semi-xl',
				link: 'text-primary underline underline-offset-2',
				alert: 'border border-error-1000 bg-error-1000 text-popover-10 hover:bg-error-800 hover:border-error-800 active:bg-error-600 active:border-error-600 disabled:bg-error-50 disabled:border-error-50 disabled:text-popover-400 hover:shadow-button-hover active:shadow-button-active',
			},
			size: {
				default: 'max-h-11 px-5 py-3',
				sm: 'h-8 w-8 rounded-full!',
				lg: 'h-10 rounded-md px-8',
				icon: 'h-11 w-11 rounded-full!',
				link: '',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		);
	},
);
Button.displayName = 'Button';

export { Button, buttonVariants };
